<template>
<div>
  <nav-bar></nav-bar>
  <div class="container order-list">
    <h1 v-if="loaded && (headers.length > 0 || hasCompletedOrders)" class="title has-text-centered">
      Open Orders
    </h1>
    <div class="level level-item">
      <div v-if="!loaded" class="is-loading level level-item">loading...</div>
      <div
        v-else-if="loaded && headers.length == 0 && !hasCompletedOrders" class=" level level-item"
      >
        There are no open or recent orders to display.
      </div>
      <div v-else-if="loaded && headers.length == 0" class=" level level-item">
        There are no open orders to display.
      </div>
      <table v-else class="table">
      <thead>
        <tr>
          <th @click="sort('order_id')"><a>Order ID <fa-icon :icon="sortIcon('order_id')"/></a></th>
          <th @click="sort('po_sm')"><a>PO/SM <fa-icon :icon="sortIcon('po_sm')"/></a></th>
          <th @click="sort('category')"><a>Product <fa-icon :icon="sortIcon('category')"/></a></th>
          <th @click="sort('status')"><a>Status <fa-icon :icon="sortIcon('status')"/></a></th>
          <th >Date</th>
          <th v-if="has_box_report">Box Reports</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(header, idx) in headers" :key="idx">
          <td>
            <router-link :to="`/customers/${customerId}/orders/${header.order_id}`" >
              {{header.order_id}}
            </router-link>
          </td>
          <td>{{header.po_sm}}</td>
          <td>{{productCategory[header.category]}}</td>
          <td :class="calculateStatusClass(header)">
            {{calculateStatus(header)}}
            <span v-if="header.status" @click="toggleShowReason(header)" class="popup">
              <fa-icon icon="info-circle"/>
              <span class="popuptext" :class="showReason[header.order_id]" @click="toggleShowReason(header)">
                This order is on hold because {{header.status}}
                </span>
            </span>
          </td>
          <td :class="calculateDateDescription(header) === 'Requested' ? 'has-text-grey' : ''">
            {{calculateDate(header)}}
            <span
              :class="calculateDateDescription(header) === 'Completed' ? 'has-text-success' : ''">
              {{calculateDateDescription(header)}}
            </span>
          </td>
          <td v-if="header.box_report"></td>
        </tr>
      </tbody>
    </table>
    </div>
    <h1 v-if="hasCompletedOrders" class="title has-text-centered order-list">
      Recently Completed Orders
    </h1>
    <prev-month
      v-for="[date, orders] in completedOrdersInOrder"
      class="prev-month"
      :key="date"
      :orders="orders"
      :date="date"
      @toggle="toggleVisibility(date)"
      :hidden='visible.includes(date)'
    >
    </prev-month>
  </div>
</div>
</template>

<script>
import PreviousMonth from '@/components/PreviousMonth.vue';
import { ordersByCustomer } from '@/utils/api';
import moment from 'moment';

export default {
  components: {
    'prev-month': PreviousMonth,
  },
  props: {
    customerId: String,
  },
  data() {
    return {
      loaded: false,
      headers: [],
      completed_orders: {},
      productCategory: {
        shades: 'Roller Shades',
        'fab shades': 'Roman Shades',
        parts: 'Components',
        drapes: 'Drapes',
        upholstery: 'Upholstery',
        motorized: 'Motorized Shades',
      },
      visible: [],
      showReason: {},
      defaultSort: 'order_id',
      currentSort: null,
      sortOrder: 'asc',
    };
  },
  methods: {
    toggleVisibility(date) {
      if (this.visible.includes(date)) {
        this.visible = this.visible.filter(e => e !== date);
      } else {
        this.visible.push(date);
      }
    },
    calculateStatus(header) {
      if (
        header.delivered_on
        && moment.duration(moment().diff(moment(header.delivered_on))).asDays() > 1) {
        return 'Delivered/Picked Up';
      }
      if (header.delivered_on) {
        return 'Delivery/Pick Up Scheduled';
      }
      if (header.completed) {
        return 'Completed';
      }
      if (header.status || header.status === '') {
        return 'On Hold';
      }
      if (header.ready_to_print) {
        if (!header.wip && ['drapes', 'fab shades'].includes(header.category)) {
          return 'Waiting for fabric';
        }
        return 'In Production';
      }
      return 'Processing';
    },
    calculateStatusClass(header) {
      const status = this.calculateStatus(header);
      let statusClass = '';
      if (status === 'Completed') {
        statusClass = 'has-background-success';
      } else if (status === 'On Hold') {
        statusClass = 'has-background-danger';
      }
      return statusClass;
    },
    calculateDate(header) {
      const DATE_FORMAT = 'MM/DD/YYYY';
      const displayDate = header.delivered_on
        || header.pickup_time
        || header.promised_date
        || header.estimated_for
        || header.requested_for
        || header.order_date;
      return moment(displayDate).isValid() ? moment.utc(displayDate).startOf('day').format(DATE_FORMAT) : '';
    },
    calculateDateDescription(header) {
      if (header.delivered_on) return 'Delivery';
      if (header.pickup_time) return 'Pick Up';
      if (header.promised_date) return 'Promised';
      if (header.estimated_for) return 'Estimated';
      if (header.requested_for) return 'Requested';
      if (header.order_date) return 'Ordered';
      return '';
    },
    unixToMonthYear(date) {
      return moment.unix(date).format('MMMM YYYY');
    },
    toggleShowReason(header) {
      if (!this.showReason[header.order_id]) {
        this.showReason = {};
        this.$set(this.showReason, header.order_id, 'show');
      } else {
        this.showReason = {};
      }
    },
    sort(by) {
      if (by === this.currentSort) {
        this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
      }
      this.currentSort = by;
      this.headers.sort((a, b) => {
        let modifier = 1;
        if (this.sortOrder === 'desc') modifier = -1;
        if (this.currentSort === 'status') {
          const statusA = this.calculateStatus(a);
          const statusB = this.calculateStatus(b);
          const statusOrder = ['On Hold', 'Waiting for fabric', 'Processing', 'In Production', 'Completed', 'Delivery/Pick Up Scheduled', 'Delivered/Picked Up'];
          if (statusOrder.indexOf(statusA) < statusOrder.indexOf(statusB)) return -1 * modifier;
          if (statusOrder.indexOf(statusA) > statusOrder.indexOf(statusB)) return 1 * modifier;
          return 0;
        }
        const valueA = this.currentSort === 'po_sm' ? a[this.currentSort].toLowerCase() : a[this.currentSort];
        const valueB = this.currentSort === 'po_sm' ? b[this.currentSort].toLowerCase() : b[this.currentSort];
        if (valueA < valueB) return -1 * modifier;
        if (valueA > valueB) return 1 * modifier;
        return 0;
      });
    },
    sortIcon(field) {
      if (field === this.currentSort) {
        return this.sortOrder === 'asc' ? 'sort-down' : 'sort-up';
      }
      return 'sort';
    },
  },
  computed: {
    has_box_report() {
      return false;
    },
    completedOrdersInOrder() {
      // eslint-disable-next-line comma-dangle, comma-spacing
      return Object.entries(this.completed_orders).sort(([date,], [dateb,]) => dateb - date);
    },
    hasCompletedOrders() {
      return this.completed_orders && Object.keys(this.completed_orders).length > 0;
    },
    searchItems() {
      const currOrders = this.headers.map(h => ({ orderId: h.order_id, poSm: h.po_sm }));
      return currOrders;
    },
  },
  watch: {
    visible: {
      handler: (val) => {
        const data = JSON.stringify(val);
        sessionStorage.setItem(`${window.location.pathname}:visible`, data);
      },
    },
  },
  async created() {
    try {
      const data = await ordersByCustomer(this.customerId);
      this.headers = data.headers
        .filter(header => !header.delivered_on
          || moment.duration(moment().diff(moment(header.delivered_on))).asDays() < 0);
      this.completed_orders = data.headers
        .filter(header => moment.duration(moment().diff(moment(header.delivered_on))).asDays() >= 0)
        .reduce((months, order) => {
          const groupVar = moment(order.delivered_on).startOf('Month').unix();
          months[groupVar] = months[groupVar] || []; // eslint-disable-line
          months[groupVar].push(order);
          return months;
        }, {});
      this.loaded = true;
      this.sort(this.defaultSort);

      // if a scroll top has been saved for this page, scroll to it
      if (sessionStorage.getItem(`${window.location.pathname}:scrollTop`)) {
        const scrollTop = JSON.parse(sessionStorage.getItem(`${window.location.pathname}:scrollTop`));
        window.setTimeout(() => window.scroll(0, scrollTop), 10);
      }
    } catch (err) {
      console.error(err);
    }
  },
  mounted() {
    // if some months have been opened on this page, open them up (changes are tracked via watcher)
    if (sessionStorage.getItem(`${window.location.pathname}:visible`)) {
      const visible = JSON.parse(sessionStorage.getItem(`${window.location.pathname}:visible`));
      this.visible = visible;
    }

    // save scrolling to sessions storage
    window.addEventListener('scroll', () => sessionStorage.setItem(`${window.location.pathname}:scrollTop`, JSON.stringify(document.documentElement.scrollTop)));
  },
};
</script>

<style scoped>
.order-list {
  margin-top: 2em;
}
.is-loading {
  margin: 12em;
}
.prev-month {
  margin: 1em;
}
.popup {
  position: relative;
  display: inline-block;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.popup .popuptext {
  visibility: hidden;
  width: 160px;
  background-color: #555;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 8px 0;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  margin-left: -80px;
}

/* Popup arrow */
.popup .popuptext::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #555 transparent transparent transparent;
}

/* Toggle this class - hide and show the popup */
.popup .show {
  visibility: visible;
  -webkit-animation: fadeIn 1s;
  animation: fadeIn 1s;
}

/* Add animation (fade in the popup) */
@-webkit-keyframes fadeIn {
  from {opacity: 0;}
  to {opacity: 1;}
}

@keyframes fadeIn {
  from {opacity: 0;}
  to {opacity:1 ;}
}
</style>
